import { useState, useEffect } from 'react';

/**
 * Custom image loader with retry logic for Next.js Image component
 *
 * @param {Object} options - Image loader options
 * @param {number} options.maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} options.retryDelay - Delay between retries in ms (default: 1000)
 * @param {number} options.timeout - Timeout for each attempt in ms (default: 10000)
 * @returns {Function} - Image loader function compatible with Next.js
 */
export function createImageLoader({
  maxRetries = 3,
  retryDelay = 1000,
  timeout = 10000
} = {}) {
  return function imageLoader({ src, width, quality }) {
    // For Cloudinary images, we can use their URL transformation API
    if (src.includes('res.cloudinary.com')) {
      const params = [`w_${width}`, `q_${quality || 75}`];
      // Check if the URL already has query parameters
      const separator = src.includes('?') ? '&' : '?';
      return `${src}${separator}${params.join(',')}`;
    }

    // For example.com images, return the original URL without parameters
    if (src.includes('example.com')) {
      return src;
    }

    // For other images, return the original URL with width and quality
    // Check if the URL already has query parameters
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}w=${width}&q=${quality || 75}`;
  };
}

/**
 * React hook for loading images with retry logic
 *
 * @param {string} src - Image source URL
 * @param {Object} options - Options for image loading
 * @param {number} options.maxRetries - Maximum number of retry attempts
 * @param {number} options.retryDelay - Delay between retries in ms
 * @param {number} options.timeout - Timeout for each attempt in ms
 * @returns {Object} - { isLoading, error, imageSrc }
 */
export function useImageWithRetry(src, {
  maxRetries = 3,
  retryDelay = 1000,
  timeout = 10000
} = {}) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    let timeoutId;

    const loadImage = async () => {
      if (!src) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Create a promise that rejects after timeout
        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error(`Image loading timed out after ${timeout}ms`));
          }, timeout);
        });

        // Create a promise for image loading
        const imagePromise = new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(src);
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = src;
        });

        // Race the promises
        const result = await Promise.race([imagePromise, timeoutPromise]);

        if (isMounted) {
          clearTimeout(timeoutId);
          setImageSrc(result);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          clearTimeout(timeoutId);
          setError(err);

          // Retry logic
          if (retryCount < maxRetries) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
            }, retryDelay);
          } else {
            setIsLoading(false);
          }
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [src, retryCount, maxRetries, retryDelay, timeout]);

  return { isLoading, error, imageSrc };
}

// Default image loader instance with standard settings
export const imageLoader = createImageLoader();

export default imageLoader;
